import { useEffect, useState, type CSSProperties } from 'react';
import { Briefcase, ChartLineDown, ChatCircleDots, Check, Coins, Compass, DotsThree, Heart, Hourglass, House, ShieldCheck, Sparkle, Sun, UserCircle } from '@phosphor-icons/react';
import type { IntentId } from './types';
import { INTRO_LOADING_PHASES, INTRO_LOADING_PHRASES, INTRO_LOADING_SUBLINE } from './loading-copy';
import { splitNarrativeParagraphs } from './report-display';
import { loadOnboardingScene, sceneIsReady, warmUpcomingScenes } from './onboarding-assets';

export function CelestialBackdrop({ scene }: { scene: string }) {
  const [paintedScene, setPaintedScene] = useState(scene);
  useEffect(() => {
    let active = true;
    void loadOnboardingScene(scene).then(ready => {
      if (active && ready) setPaintedScene(scene);
      if (active) warmUpcomingScenes(scene);
    });
    return () => { active = false; };
  }, [scene]);
  const visibleScene = sceneIsReady(scene) ? scene : paintedScene;
  return <div className={`celestial-backdrop scene-${visibleScene}`} data-scene={visibleScene} aria-hidden="true"><div className="celestial-stardust" /></div>;
}

export function OnboardingStepper({ step }: { step: number }) {
  return <ol className="celestial-stepper" aria-label="Шаги настройки профиля">
    {['Интересы', 'Имя', 'Дата рождения', 'Запрос'].map((label, index) => <li key={label} className={index < step ? 'complete' : index === step ? 'current' : ''} aria-current={index === step ? 'step' : undefined}>
      <span className="step-orb" aria-hidden="true">{index < step ? <Check weight="bold" /> : index + 1}</span>
      <span>{label}</span><span className="sr-only">{index < step ? ', завершено' : `, шаг ${index + 1} из 4`}</span>
    </li>)}
  </ol>;
}

const CHOICE_ICONS = {
  relationships: [Heart, Sun, ChatCircleDots, Hourglass, Sparkle],
  money: [Coins, Hourglass, ChartLineDown, ShieldCheck, Briefcase],
  career: [Briefcase, Hourglass, Sparkle, Coins, Compass],
  decision: [Heart, Briefcase, Coins, House],
  self: [Sparkle, Compass, ShieldCheck, Sun],
  other: [ChatCircleDots],
};

export function RequestChoiceIcon({ intent, index, other }: { intent: IntentId; index: number; other: boolean }) {
  const Icon = other ? DotsThree : CHOICE_ICONS[intent][index] ?? UserCircle;
  return <span className="choice-medallion" aria-hidden="true"><Icon weight="light" /></span>;
}

/** Presentation only: preserve every word and number, split long prose at sentence boundaries. */
export function IntroParagraphs({ text, className = '' }: { text: string; className?: string }) {
  const paragraphs = splitNarrativeParagraphs(text).flatMap(paragraph => {
    if (paragraph.length < 310) return [paragraph];
    const sentences = paragraph.match(/[^.!?]+(?:[.!?]+(?:[»”"])?(?:\s+|$)|$)/g) ?? [paragraph];
    // Do not reinterpret text if a punctuation pattern could not be preserved exactly.
    if (sentences.join('').trim() !== paragraph.trim()) return [paragraph];
    const chunks: string[] = [];
    let chunk = '';
    for (const sentence of sentences) {
      chunk += sentence;
      if (chunk.length >= 180) { chunks.push(chunk.trim()); chunk = ''; }
    }
    if (chunk.trim()) chunks.push(chunk.trim());
    return chunks;
  });
  return <div className={`intro-paragraphs ${className}`}>{paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}</div>;
}

export function ReadingGeneration({ elapsed }: { elapsed: number }) {
  const phraseIndex = Math.min(Math.floor(elapsed / 4), INTRO_LOADING_PHRASES.length - 1);
  const progress = Math.min(92, 18 + elapsed * 4);
  return <div className="generation-page celestial-generation celestial-surface" aria-busy="true">
    <CelestialBackdrop scene="generation" />
    <div className="celestial-dial" aria-hidden="true">
      <div className="dial-ring outer" /><div className="dial-ring middle" /><div className="dial-ring inner" />
      <div className="dial-axis" /><div className="dial-axis horizontal" />
      <div className="dial-numbers">{[9, 7, 2, 6, 1, 3].map((number, index) => <span key={number} style={{ '--angle': `${index * 60}deg` } as CSSProperties}><i>{number}</i></span>)}</div>
      <div className="planet-track"><i /></div><div className="planet-track second"><i /></div>
      <span className="dial-star" /><span className="dial-star diagonal" />
    </div>
    <div className="generation-copy">
      <p className="eyebrow">Твой первый разбор</p>
      <h1 aria-live="polite">{elapsed > 18 ? 'Ещё немного - собираем цельную картину' : INTRO_LOADING_PHRASES[phraseIndex]}</h1>
      <p>{INTRO_LOADING_SUBLINE}</p>
      <div className="liquid-progress-row">
        <div className="liquid-progress" role="progressbar" aria-label="Подготовка разбора" aria-valuetext="Разбор готовится" aria-valuemin={0} aria-valuemax={100}>
          <div className="liquid-level" style={{ width: `${progress}%` }}><span className="liquid-wave" /><span className="liquid-shine" /></div>
        </div><span className="progress-percent" aria-hidden="true">{progress}%</span>
      </div>
      <ol className="celestial-generation-steps">{INTRO_LOADING_PHASES.map((label, index) => <li key={label} className={index === 0 || (index === 1 && elapsed > 4) ? 'done' : 'pending'}><span aria-hidden="true">{index === 0 || (index === 1 && elapsed > 4) ? <Check /> : <i />}</span>{label}</li>)}</ol>
    </div>
  </div>;
}
